import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, CreditCard, PackageCheck, Repeat2, Store } from "lucide-react";
import { Button } from "@/components/ui/button";
import { whatsappLinks } from "@/lib/whatsapp";

const COLORS = [
  { name: "Schwarz", hex: "#1B1B1D" },
  { name: "Weiß", hex: "#EDE9E2" },
  { name: "Blau", hex: "#A9C4E8" },
  { name: "Bordeaux", hex: "#6E1B2C" },
];

const PERKS = [
  { icon: CreditCard, t: "Raten nach Wunsch", d: "Laufzeit & Anzahlung frei wählbar" },
  { icon: Repeat2, t: "Altgerät zahlt mit", d: "Eintausch direkt gegengerechnet" },
  { icon: PackageCheck, t: "Sofort verfügbar", d: "Farbe & Speicher kurz anfragen" },
  { icon: Store, t: "Beratung vor Ort", d: "Persönlich in Wien, 1170" },
];

export default function Iphone18ProLaunch() {
  const wa = whatsappLinks.preorder({ model: "iPhone 18 Pro" });

  return (
    <section
      className="relative overflow-hidden bg-black border-y border-white/5"
      data-testid="iphone18-launch-section"
    >
      {/* ambient depth — kept on the copy side so the device shot stays pure black */}
      <div className="absolute -top-40 -left-32 w-[620px] h-[620px] rounded-full bg-[#DC1928]/12 blur-[150px] pointer-events-none"></div>
      <div className="absolute -bottom-56 left-1/4 w-[520px] h-[520px] rounded-full bg-[#3D5BFF]/8 blur-[150px] pointer-events-none"></div>

      <div className="max-w-7xl mx-auto px-6 lg:px-12 relative py-16 md:py-24">
        <div className="grid md:grid-cols-12 gap-10 lg:gap-6 items-center">
          {/* ── Copy ─────────────────────────────── */}
          <motion.div
            initial={{ opacity: 0, y: 26 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="md:col-span-6 lg:col-span-5"
          >
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#DC1928]/12 border border-[#DC1928]/35 backdrop-blur">
              <span className="w-1.5 h-1.5 rounded-full bg-[#FF2D40] pulse-red"></span>
              <span className="text-[10px] uppercase tracking-[0.24em] text-[#FF2D40] font-bold">
                Neu eingetroffen
              </span>
            </div>

            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-white mt-5 leading-[1.02]">
              iPhone 18 Pro
              <br />
              <span className="bg-gradient-to-r from-[#FF2D40] via-[#DC1928] to-[#FF6A78] bg-clip-text text-transparent">
                hier erhältlich.
              </span>
            </h2>

            <p className="mt-5 text-base md:text-lg text-zinc-400 leading-relaxed max-w-lg">
              Ab sofort bei uns erhältlich — sag uns Farbe &amp; Speichergröße, wir
              halten dein Wunschmodell bereit.{" "}
              <span className="text-white font-medium">
                Und du finanzierst dein 18&nbsp;Pro so, wie du es gern hast.
              </span>
            </p>

            {/* colors */}
            <div className="mt-7 flex flex-wrap items-center gap-2.5" data-testid="iphone18-colors">
              {COLORS.map((c) => (
                <div
                  key={c.name}
                  className="group flex items-center gap-2 pl-1.5 pr-3 py-1.5 rounded-full bg-white/[0.04] border border-white/10 hover:border-white/25 transition-colors"
                  data-testid={`iphone18-color-${c.name.toLowerCase()}`}
                >
                  <span
                    className="w-4 h-4 rounded-full ring-1 ring-inset ring-white/25"
                    style={{ background: c.hex }}
                  ></span>
                  <span className="text-xs text-zinc-300 group-hover:text-white transition-colors">
                    {c.name}
                  </span>
                </div>
              ))}
            </div>

            {/* perks */}
            <div className="mt-8 grid sm:grid-cols-2 gap-3">
              {PERKS.map((p, i) => (
                <div
                  key={p.t}
                  className="flex items-start gap-3 p-3.5 rounded-xl bg-white/[0.03] border border-white/5 hover:border-[#DC1928]/30 transition-colors"
                  data-testid={`iphone18-perk-${i}`}
                >
                  <div className="shrink-0 w-8 h-8 rounded-lg bg-[#DC1928]/15 border border-[#DC1928]/25 flex items-center justify-center">
                    <p.icon className="w-4 h-4 text-[#FF4F5E]" />
                  </div>
                  <div className="min-w-0">
                    <div className="text-sm text-white font-medium leading-tight">{p.t}</div>
                    <div className="text-[11px] text-zinc-500 leading-tight mt-0.5">{p.d}</div>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Link to="/kontakt?anfrage=iphone-18-pro">
                <Button
                  className="w-full sm:w-auto bg-[#DC1928] hover:bg-[#B01829] text-white rounded-full h-12 px-7 text-base btn-lift glow-red"
                  data-testid="iphone18-inquiry-cta"
                >
                  Jetzt anfragen <ArrowRight className="w-4 h-4 ml-1.5" />
                </Button>
              </Link>
              <a href={wa.href} onClick={wa.onClick} target="_blank" rel="noreferrer">
                <Button
                  variant="outline"
                  className="w-full sm:w-auto rounded-full h-12 px-7 text-base bg-white/5 border-white/15 text-white hover:bg-white/10 hover:text-white btn-lift"
                  data-testid="iphone18-finance-cta"
                >
                  Finanzierung anfragen
                </Button>
              </a>
            </div>

            <p className="mt-4 text-[11px] text-zinc-600">
              Unverbindliche Anfrage · Finanzierung abhängig von Tarif &amp; Bonität ·
              Verfügbarkeit je nach Farbe &amp; Speicher
            </p>
          </motion.div>

          {/* ── Device visual — black studio shot, edges dissolved into the page ── */}
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.9, delay: 0.12, ease: [0.22, 1, 0.36, 1] }}
            className="md:col-span-6 lg:col-span-7 relative"
          >
            <motion.div
              animate={{ y: [0, -9, 0] }}
              transition={{ duration: 7.5, repeat: Infinity, ease: "easeInOut" }}
              className="relative md:-mr-8 lg:-mr-20 md:scale-[1.08] lg:scale-[1.14] origin-center"
            >
              <img
                src="/images/store/iphone-18-pro-lineup-black.jpg"
                alt="iPhone 18 Pro in Schwarz, Weiß, Blau und Bordeaux — ab sofort bei Haus des Handys in Wien erhältlich"
                className="w-full h-auto select-none"
                data-testid="iphone18-hero-image"
                loading="lazy"
                style={{
                  maskImage:
                    "radial-gradient(ellipse 74% 72% at 50% 50%, #000 58%, rgba(0,0,0,0.55) 82%, transparent 100%)",
                  WebkitMaskImage:
                    "radial-gradient(ellipse 74% 72% at 50% 50%, #000 58%, rgba(0,0,0,0.55) 82%, transparent 100%)",
                }}
              />
            </motion.div>

            <div className="mt-2 md:mt-0 flex flex-wrap items-center justify-center gap-2 md:absolute md:bottom-2 md:left-1/2 md:-translate-x-1/2">
              <div
                className="px-3 py-1.5 rounded-full bg-white/[0.05] border border-white/10 backdrop-blur text-[11px] text-zinc-300"
                data-testid="iphone18-badge-storage"
              >
                256 GB – 2 TB
              </div>
              <div
                className="px-3 py-1.5 rounded-full bg-[#DC1928]/12 border border-[#DC1928]/30 backdrop-blur text-[11px] text-[#FF8A95]"
                data-testid="iphone18-badge-finance"
              >
                Verfügbar &amp; finanzierbar
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
